import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AppWindow, Plus, Wrench, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { PriorityBadge, WorkOrderStatusBadge } from '../components/work-order-badges';
import { WorkOrderFormModal } from '../components/WorkOrderFormModal';
import { Badge, Button, Card, EmptyState, ErrorState, Input, PageHeader, Pagination, Select, Spinner, Table, Tabs, Td, Th } from '../components/ui';
import { api } from '../lib/api';
import { indentedName, SOURCE_LABEL, useDepartments } from '../lib/automation';
import { useDesk } from '../lib/desk';
import { equipmentLabel, FACILITY_SOURCE_LABEL, placeLabel, useFacilityPlaces } from '../lib/facilities';
import { formatDue, formatRelative } from '../lib/format';
import { useDebouncedValue, useUrlState } from '../lib/hooks';
import type { Page, WorkOrder, WorkOrderCategory, WorkOrderPriority, WorkOrderSource, WorkOrderSummary } from '../lib/types';
import { OPEN_WORK_ORDER_STATUSES, WORK_ORDER_CATEGORY_LABEL, WORK_ORDER_PRIORITY_LABEL } from '../lib/work-orders';

const PAGE_SIZE = 25;
const DEFAULTS = { view: 'open', search: '', priority: '', category: '', departmentId: '', buildingId: '', assignee: '', page: '1' };
type View = 'open' | 'mine' | 'unassigned' | 'overdue' | 'closed' | 'all';

const VIEW_QUERY: Record<View, { state: string; assignedTo?: string; overdue?: boolean }> = {
  open: { state: 'open' },
  mine: { state: 'open', assignedTo: 'me' },
  unassigned: { state: 'open', assignedTo: 'unassigned' },
  overdue: { state: 'open', overdue: true },
  closed: { state: 'closed' },
  all: { state: 'all' },
};

/** Tickets opened by automation or a schedule rather than a person. */
const AUTOMATED_SOURCES: WorkOrderSource[] = ['SMART_RULE', 'RECURRING', 'PREVENTIVE', 'INSPECTION'];

const isOverdue = (ticket: WorkOrder) => !!ticket.dueAt && new Date(ticket.dueAt) < new Date() && OPEN_WORK_ORDER_STATUSES.includes(ticket.status);

/** The work order queue for the technology help desk, or facilities under /facilities. */
export function WorkOrdersPage() {
  const desk = useDesk();
  const facilities = desk.facilities;
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput, 300);
  const [creating, setCreating] = useState(false);
  const departments = useDepartments();
  const places = useFacilityPlaces(facilities);

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const views: View[] = ['open', 'mine', 'unassigned', 'overdue', 'closed', 'all'];
  const view = (views.includes(filters.view as View) ? filters.view : 'open') as View;
  const page = Number(filters.page) || 1;

  const summary = useQuery({
    queryKey: ['work-orders', 'summary', desk.service],
    queryFn: () => api.get<WorkOrderSummary>('/work-orders/summary', desk.query()),
    refetchInterval: facilities ? 60_000 : false,
  });
  const list = useQuery({
    queryKey: ['work-orders', 'list', desk.service, filters],
    placeholderData: keepPreviousData,
    refetchInterval: facilities ? 60_000 : false,
    queryFn: () =>
      api.get<Page<WorkOrder>>(
        '/work-orders',
        desk.query({
          ...VIEW_QUERY[view],
          assignedTo: filters.assignee || VIEW_QUERY[view].assignedTo,
          search: filters.search,
          priority: filters.priority,
          category: facilities ? undefined : filters.category,
          departmentId: filters.departmentId,
          buildingId: facilities ? filters.buildingId : undefined,
          page,
          pageSize: PAGE_SIZE,
        }),
      ),
  });

  const filteredAssignee = filters.assignee ? list.data?.items.find((t) => t.assignedToId === filters.assignee)?.assignedTo?.name : null;
  const departmentWord = desk.departmentWord;

  return (
    <>
      <PageHeader
        title="Work orders"
        description={
          facilities
            ? 'Maintenance requests from staff, work the facilities team logs, and preventive maintenance and inspection follow-ups, across every building.'
            : 'Problems reported by teachers and staff, repairs logged by the technology team, and work opened by automation.'
        }
        actions={
          desk.can('TECHNICIAN') && (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              New work order
            </Button>
          )
        }
      />

      <Tabs<View>
        className="mb-4"
        value={view}
        onChange={(value) => setFilters({ view: value })}
        tabs={[
          { value: 'open', label: 'Open', count: summary.data?.open },
          { value: 'mine', label: 'Assigned to me', count: summary.data?.assignedToMe },
          { value: 'unassigned', label: 'Unassigned', count: summary.data?.unassigned },
          { value: 'overdue', label: 'Overdue', count: summary.data?.overdue },
          { value: 'closed', label: 'Resolved & closed' },
          { value: 'all', label: 'All' },
        ]}
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={facilities ? 'Search #, request, requester, location, equipment' : 'Search #, problem, requester, asset tag, software'}
            className="w-full sm:w-72"
          />
          <Select value={filters.priority} onChange={(e) => setFilters({ priority: e.target.value })} className="w-auto">
            <option value="">Any priority</option>
            <option value="HIGH,URGENT">High and urgent</option>
            {(Object.keys(WORK_ORDER_PRIORITY_LABEL) as WorkOrderPriority[]).map((priority) => (
              <option key={priority} value={priority}>
                {WORK_ORDER_PRIORITY_LABEL[priority]}
              </option>
            ))}
          </Select>
          {facilities ? (
            <Select value={filters.buildingId} onChange={(e) => setFilters({ buildingId: e.target.value })} className="w-auto" aria-label="Building">
              <option value="">Every building</option>
              {places.data?.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </Select>
          ) : (
            <Select value={filters.category} onChange={(e) => setFilters({ category: e.target.value })} className="w-auto">
              <option value="">Any type of problem</option>
              {(Object.keys(WORK_ORDER_CATEGORY_LABEL) as WorkOrderCategory[]).map((category) => (
                <option key={category} value={category}>
                  {WORK_ORDER_CATEGORY_LABEL[category]}
                </option>
              ))}
            </Select>
          )}
          {!!departments.data?.length && (
            <Select
              value={filters.departmentId}
              onChange={(e) => setFilters({ departmentId: e.target.value })}
              className="w-auto"
              aria-label={facilities ? 'Category' : 'Department'}
            >
              <option value="">Any {departmentWord}</option>
              <option value="none">No {departmentWord}</option>
              {departments.data.map((department) => (
                <option key={department.id} value={department.id}>
                  {indentedName(department)}
                </option>
              ))}
            </Select>
          )}
          {filters.assignee && (
            <button
              type="button"
              onClick={() => setFilters({ assignee: '' })}
              className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-800 hover:bg-brand-100"
            >
              Assigned to {filteredAssignee ?? 'one person'}
              <X className="size-3" aria-label="Clear" />
            </button>
          )}
          {list.isFetching && !list.isLoading && <Spinner className="size-4" />}
        </div>

        {list.isLoading ? (
          <div className="flex h-60 items-center justify-center">
            <Spinner />
          </div>
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : list.data!.items.length === 0 ? (
          <EmptyState
            icon={<Wrench />}
            title={view === 'open' ? 'No open work orders' : view === 'overdue' ? 'Nothing is overdue' : 'No work orders found'}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{facilities ? 'Request' : 'Problem'}</Th>
                  <Th>{facilities ? 'Where' : 'Device'}</Th>
                  <Th>Reported by</Th>
                  <Th>Priority</Th>
                  <Th>Status</Th>
                  <Th>Assigned to</Th>
                  <Th>{view === 'closed' || view === 'all' ? 'Updated' : 'Opened'}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {list.data!.items.map((ticket) => {
                  const departmentPath = departments.data?.find((d) => d.id === ticket.departmentId)?.path ?? ticket.department?.name;
                  const overdue = isOverdue(ticket);
                  const dueSoon = !overdue && !!ticket.dueAt && OPEN_WORK_ORDER_STATUSES.includes(ticket.status);
                  return (
                    <tr key={ticket.id} className="cursor-pointer hover:bg-slate-50/80" onClick={() => navigate(desk.workOrderHref(ticket.id))}>
                      <Td className="max-w-xs">
                        <Link
                          to={desk.workOrderHref(ticket.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="block truncate font-medium text-slate-900 hover:text-brand-700"
                        >
                          <span className="text-slate-400">#{ticket.number}</span> {ticket.title}
                        </Link>
                        <p className="truncate text-xs text-slate-500">
                          {(facilities ? [departmentPath] : [WORK_ORDER_CATEGORY_LABEL[ticket.category], departmentPath]).filter(Boolean).join(' · ')}
                        </p>
                        {(ticket.escalationLevel > 0 || ticket.source === 'EMAIL' || AUTOMATED_SOURCES.includes(ticket.source) || overdue || dueSoon) && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {ticket.escalationLevel > 0 && <Badge tone="red">Escalated</Badge>}
                            {overdue && (
                              <span title={`Due ${formatDue(ticket.dueAt)}`}>
                                <Badge tone="red">Overdue</Badge>
                              </span>
                            )}
                            {dueSoon && <Badge tone="amber">Due {formatDue(ticket.dueAt)}</Badge>}
                            {ticket.source === 'EMAIL' && <Badge tone="blue">{SOURCE_LABEL.EMAIL}</Badge>}
                            {AUTOMATED_SOURCES.includes(ticket.source) && (
                              <Badge tone="violet">{facilities ? FACILITY_SOURCE_LABEL[ticket.source] : SOURCE_LABEL[ticket.source]}</Badge>
                            )}
                          </div>
                        )}
                      </Td>
                      <Td className="max-w-[14rem]">
                        {facilities ? (
                          <>
                            <p className="truncate text-slate-700">
                              {placeLabel({ building: ticket.building ?? ticket.room?.building, room: ticket.room, locationDetail: ticket.locationDetail }) || (
                                <span className="text-slate-400">—</span>
                              )}
                            </p>
                            {ticket.facilityAsset && (
                              <Link
                                to={`/facilities/equipment/${ticket.facilityAsset.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="block truncate text-xs text-brand-700 hover:underline"
                              >
                                {equipmentLabel(ticket.facilityAsset)}
                              </Link>
                            )}
                          </>
                        ) : ticket.asset ? (
                          <>
                            <Link
                              to={`/assets/${ticket.asset.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="font-mono text-xs font-semibold text-brand-800 hover:underline"
                            >
                              {ticket.asset.assetTag}
                            </Link>
                            <p className="truncate text-xs text-slate-500">{ticket.asset.name}</p>
                          </>
                        ) : ticket.software ? (
                          <p className="flex min-w-0 items-center gap-1.5 text-xs text-slate-700">
                            <AppWindow className="size-3.5 shrink-0 text-slate-400" />
                            <span className="truncate">{ticket.software.name}</span>
                          </p>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </Td>
                      <Td className="max-w-[12rem]">
                        <p className="truncate text-slate-800">{ticket.requesterName}</p>
                        {!facilities && ticket.room && <p className="truncate text-xs text-slate-500">{ticket.room.name}</p>}
                      </Td>
                      <Td>
                        <PriorityBadge priority={ticket.priority} />
                      </Td>
                      <Td>
                        <WorkOrderStatusBadge status={ticket.status} />
                      </Td>
                      <Td className="whitespace-nowrap text-slate-600">{ticket.assignedTo?.name ?? <span className="text-slate-400">Unassigned</span>}</Td>
                      <Td className="whitespace-nowrap text-slate-500">
                        {formatRelative(view === 'closed' || view === 'all' ? ticket.updatedAt : ticket.createdAt)}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={list.data!.total} onPage={(p) => setFilters({ page: String(p) }, { resetPage: false })} />
          </>
        )}
      </Card>

      <WorkOrderFormModal open={creating} onClose={() => setCreating(false)} staff onCreated={(ticket) => navigate(desk.workOrderHref(ticket.id))} />
    </>
  );
}
